import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:6200'

function TenancySwitcherDialog({ tenancies, activeTenancyId, onClose, onSwitch }) {
  const [searchQuery, setSearchQuery] = useState('')

  const filteredTenancies = tenancies.filter(tenancy =>
    tenancy.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[#111418] text-xl font-bold">Switch Workspace</h3>
          <button
            onClick={onClose}
            className="text-[#617589] hover:text-[#111418] transition-colors"
          >
            <span className="material-symbols-outlined text-2xl">close</span>
          </button>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="flex items-center gap-2 rounded-lg border border-[#dbe0e6] bg-white h-10 px-3">
            <span className="material-symbols-outlined text-[#617589] text-[20px]">search</span>
            <input
              className="flex-1 bg-transparent border-none outline-none text-[#111418] placeholder:text-[#617589] text-sm"
              placeholder="Search workspaces..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Tenancy List */}
        <div className="max-h-80 overflow-y-auto space-y-1 mb-4">
          {filteredTenancies.map((tenancy) => (
            <button
              key={tenancy.id}
              onClick={() => onSwitch(tenancy.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                tenancy.id === activeTenancyId
                  ? 'bg-primary/10 text-primary'
                  : 'hover:bg-gray-50 text-[#111418]'
              }`}
            >
              {/* Icon based on type */}
              <div className={`w-8 h-8 rounded flex items-center justify-center ${
                tenancy.tenancy_type === 'CLIENT'
                  ? 'bg-blue-100'
                  : 'bg-purple-100'
              }`}>
                <span className={`material-symbols-outlined text-[18px] ${
                  tenancy.tenancy_type === 'CLIENT'
                    ? 'text-blue-600'
                    : 'text-purple-600'
                }`}>
                  {tenancy.tenancy_type === 'CLIENT' ? 'business' : 'groups'}
                </span>
              </div>

              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{tenancy.name}</p>
                <p className="text-xs text-[#617589] truncate">
                  {tenancy.tenancy_type === 'CLIENT' ? 'Client' : 'Service Provider'}
                </p>
              </div>

              {tenancy.id === activeTenancyId && (
                <span className="material-symbols-outlined text-primary text-[20px]">check_circle</span>
              )}
            </button>
          ))}

          {filteredTenancies.length === 0 && (
            <div className="text-center py-8 text-[#617589]">
              <span className="material-symbols-outlined text-4xl mb-2">search_off</span>
              <p className="text-sm">No workspaces found</p>
            </div>
          )}
        </div>

        {/* Create New Workspace */}
        <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border-2 border-dashed border-[#dbe0e6] hover:border-primary hover:bg-primary/5 text-[#617589] hover:text-primary transition-colors">
          <span className="material-symbols-outlined text-[20px]">add_circle</span>
          <span className="text-sm font-semibold">Create New Workspace</span>
        </button>
      </div>
    </div>
  )
}

export default function TenancySwitcher({ tenancies, activeTenancyId }) {
  const { token, updateUser } = useAuth()
  const navigate = useNavigate()
  const [showDialog, setShowDialog] = useState(false)
  const [switching, setSwitching] = useState(false)

  const handleSwitch = async (tenancyId) => {
    if (tenancyId === activeTenancyId) {
      setShowDialog(false)
      return
    }

    setSwitching(true)
    try {
      const response = await fetch(`${API_URL}/api/v1/me/active-tenancy`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ active_tenancy_id: tenancyId }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to switch workspace')
      }

      // Update user context
      updateUser(data)
      setShowDialog(false)

      // Navigate to the new tenancy's dashboard
      const newTenancy = tenancies.find(t => t.id === tenancyId)
      if (newTenancy?.slug) {
        navigate(`/${newTenancy.slug}`)
      } else {
        // Fallback to reload if slug not available
        window.location.reload()
      }
    } catch (err) {
      console.error('Failed to switch tenancy:', err)
      alert('Failed to switch workspace. Please try again.')
    } finally {
      setSwitching(false)
    }
  }

  const activeTenancy = tenancies.find(t => t.id === activeTenancyId)
  const showInline = tenancies.length < 5

  if (showInline) {
    // Show all tenancies inline
    return (
      <>
        <div className="text-xs text-[#617589] font-semibold uppercase tracking-wider px-3 mb-2">
          Active Workspace:
        </div>
        {tenancies.map((tenancy) => (
          <button
            key={tenancy.id}
            onClick={() => handleSwitch(tenancy.id)}
            disabled={switching}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
              tenancy.id === activeTenancyId
                ? 'bg-primary/10 text-primary pointer-events-none'
                : 'text-[#111418] hover:bg-gray-50'
            }`}
          >
            {tenancy.id === activeTenancyId && (
              <span className="material-symbols-outlined text-[18px]">check</span>
            )}
            <span className="text-sm font-medium truncate">
              {tenancy.name}
            </span>
          </button>
        ))}
      </>
    )
  }

  // Show current tenancy + link to dialog
  return (
    <>
      <div className="text-xs text-[#617589] font-semibold uppercase tracking-wider px-3 mb-2">
        Active Workspace:
      </div>
      <div className="px-3 py-2 rounded-lg bg-primary/10 mb-1">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-primary text-[18px]">check</span>
          <span className="text-sm font-medium text-primary truncate">
            {activeTenancy?.name || 'Unknown'}
          </span>
        </div>
      </div>
      <button
        onClick={() => setShowDialog(true)}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-[#111418] hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm font-medium">Switch Workspace...</span>
        <span className="material-symbols-outlined text-[20px]">arrow_forward</span>
      </button>

      {showDialog && (
        <TenancySwitcherDialog
          tenancies={tenancies}
          activeTenancyId={activeTenancyId}
          onClose={() => setShowDialog(false)}
          onSwitch={handleSwitch}
        />
      )}
    </>
  )
}
